import * as React from "react";
import { styled } from "@mui/material/styles";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, {
  AccordionSummaryProps,
  accordionSummaryClasses,
} from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  Grid2,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import {
  FollowUpType,
  ListOfFollowUpType,
} from "src/interface/enum/FollowUpType";
import { FormikProps } from "formik";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import moment from "jalali-moment";

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&::before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowBackIosIcon sx={{ fontSize: "0.9rem" }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: "rgba(0, 0, 0, .03)",
  flexDirection: "row-reverse",
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    {
      transform: "rotate(90deg)",
    },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(1),
  },
  ...theme.applyStyles("dark", {
    backgroundColor: "rgba(255, 255, 255, .05)",
  }),
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

export default function FollowupForm({ formik }: { formik: FormikProps<any> }) {
  const [expanded, setExpanded] = React.useState<string | false>();

  React.useEffect(() => {
    if (formik.values.isFollowUp === "true") {
      setExpanded("panel1");
    }
  }, [formik.values.isFollowUp]);

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
      formik.setFieldValue("isFollowUp", newExpanded.toString());

      // اگر accordion بسته شد، فیلدهای مربوطه را پاک کن
      if (!newExpanded) {
        formik.setFieldValue("followUpType", undefined);
        formik.setFieldValue("firstFollowUpedOn", undefined);
        formik.setFieldValue("alertDayLeft1", undefined);
        formik.setFieldValue("alertDayLeft2", undefined);
        formik.setFieldValue("alertDayLeft3", undefined);
        formik.setFieldValue("alertTimeAt", "");
      }
    };

  const [menuItems, setMenuItems] = React.useState<JSX.Element[]>([]);

  React.useEffect(() => {
    const items: JSX.Element[] = []; // اینجا نوع را مشخص کرده‌ایم
    for (let i = 1; i <= 30; i++) {
      items.push(
        <MenuItem key={i} value={i}>
          {i} روز مانده
        </MenuItem>
      );
    }
    setMenuItems(items); // بروزرسانی state با استفاده از setMenuItems
  }, []);

  const [alertTime, setAlertTime] = React.useState<Date>();
  React.useEffect(() => {
    if (formik.values.alertTimeAt) {
      const currentDate = new Date();
      const [hours, minutes, seconds] = formik.values.alertTimeAt
        ?.split(":")
        ?.map(Number);
      currentDate.setHours(hours);
      currentDate.setMinutes(minutes);
      currentDate.setSeconds(seconds);
      setAlertTime(currentDate);
    } else {
      setAlertTime(undefined);
    }
  }, [formik.values.alertTimeAt]);

  const [getNextDate, setNextDate] = React.useState("");
  React.useEffect(() => {
    if (formik.values.firstFollowUpedOn && formik.values.followUpType) {
      let alert1 = 0,
        alert2 = 0,
        alert3 = 0;
      if (formik.values.alertDayLeft1 && formik.values.alertDayLeft1 > 0) {
        alert1 = formik.values.alertDayLeft1;
      }
      if (formik.values.alertDayLeft2 && formik.values.alertDayLeft2 > 0) {
        alert2 = formik.values.alertDayLeft2;
      }
      if (formik.values.alertDayLeft3 && formik.values.alertDayLeft3 > 0) {
        alert3 = formik.values.alertDayLeft3;
      }
      // پیدا کردن بزرگترین هشدار
      const alerts = [alert1, alert2, alert3]; // فیلتر کردن هشدارهای معتبر
      let largestAlert = Math.max(...alerts); // بزرگترین هشدار
      // فرض کنید تاریخ‌ها به فرمت Date جاوااسکریپت تعریف شده‌اند
      let startDate = new Date(formik.values.firstFollowUpedOn); // تاریخ شروع
      let currentDate = new Date(); // تاریخ امروز
      let dateLater = new Date(startDate);
      const followType = parseInt(formik.values.followUpType, 10);
      const getMonthNumber =
        followType === FollowUpType.Fasli
          ? 3
          : followType === FollowUpType.Monthly
            ? 1
            : 12;
      dateLater.setMonth(startDate.getMonth() + getMonthNumber); // تاریخ یک ماه پس از تاریخ شروع
      let timeDifference = dateLater.getTime() - currentDate.getTime(); // تفاوت زمانی بر حسب میلی‌ثانیه
      let daysLeft = Math.ceil(timeDifference / (1000 * 3600 * 24));
      if (daysLeft < largestAlert) {
        // فیلتر کردن هشدارهایی که بیشتر از daysLeft هستند
        const nextAlert = alerts.filter((alert) => alert <= daysLeft);
        console.log("🚀 ~ React.useEffect ~ nextAlert:", nextAlert);
        if (nextAlert.length > 0) {
          // بزرگترین هشدار که از daysLeft بزرگتر است را پیدا می‌کنیم
          largestAlert = Math.max(...nextAlert);
          console.log("حداکثر هشدار بزرگتر از daysLeft: ", largestAlert);
        }
      }
      if (daysLeft >= largestAlert) {
        dateLater.setDate(dateLater.getDate() - largestAlert);
      }
      setNextDate(moment(new Date(dateLater)).format("YYYY-MM-DD"));
    }
  }, [
    formik.values.firstFollowUpedOn,
    formik.values.followUpType,
    formik.values.alertDayLeft1,
    formik.values.alertDayLeft2,
    formik.values.alertDayLeft3,
  ]);

  return (
    <div>
      <Accordion
        expanded={expanded === "panel1"}
        onChange={handleChange("panel1")}
      >
        <AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            width={"100%"}
          >
            <Typography>
              نیاز به پیگیری: {expanded === "panel1" ? "دارد" : "ندارد"}
            </Typography>
            <Typography>
              {expanded === "panel1"
                ? "تاریخ ارسال پیامک بعدی:" +
                  (getNextDate
                    ? moment
                        .from(getNextDate, "en", "YYYY-MM-DD")
                        .format("jYYYY/jMM/jDD")
                    : "-")
                : ""}
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Grid2 container spacing={1} marginBottom={1}>
            <Grid2 size={2}>
              <Autocomplete
                value={
                  ListOfFollowUpType.find(
                    (f) => f.value === parseInt(formik.values.followUpType, 10)
                  ) || null
                }
                onChange={(event: any, newValue: any) => {
                  formik.setFieldValue(
                    "followUpType",
                    newValue?.value.toString() || undefined
                  );
                }}
                options={ListOfFollowUpType.filter((f) => f.require)}
                fullWidth
                renderInput={(params) => (
                  <TextField
                    error={
                      formik.touched.followUpType &&
                      Boolean(formik.errors.followUpType)
                    }
                    helperText={
                      (formik.touched.followUpType &&
                        formik.errors.followUpType) as boolean
                    }
                    {...params}
                    label="نوع پیگیری"
                    placeholder="نوع پیگیری"
                  />
                )}
              />
            </Grid2>
            <Grid2 size={2}>
              <DatePicker
                label="تاریخ اولین سر رسید"
                onChange={(date: Date | null) =>
                  formik.setFieldValue("firstFollowUpedOn", date)
                }
                sx={{ width: "100%" }}
                views={["day", "month"]}
                //@ts-ignore
                value={formik.values.firstFollowUpedOn}
              />
              <FormHelperText
                error={
                  formik.touched.firstFollowUpedOn &&
                  Boolean(formik.errors.firstFollowUpedOn)
                }
              >
                {
                  (formik.touched.firstFollowUpedOn &&
                    formik.errors.firstFollowUpedOn) as boolean
                }
              </FormHelperText>
            </Grid2>
            <Grid2 size={2}>
              <FormControl
                fullWidth
                error={
                  formik.touched.alertDayLeft1 &&
                  Boolean(formik.errors.alertDayLeft1)
                }
              >
                <InputLabel id="demo-simple-select-helper-label">
                  پیامک اول
                </InputLabel>
                <Select
                  fullWidth
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  value={menuItems.length > 0 && formik.values.alertDayLeft1}
                  label="پیامک اول"
                  onChange={(event, newValue) =>
                    formik.setFieldValue(
                      "alertDayLeft1",
                      //@ts-ignore
                      newValue?.props?.value
                    )
                  }
                >
                  <MenuItem value="">
                    <em>هیچی</em>
                  </MenuItem>
                  {menuItems}
                </Select>
                <FormHelperText>
                  {
                    (formik.touched.alertDayLeft1 &&
                      formik.errors.alertDayLeft1) as boolean
                  }
                </FormHelperText>
              </FormControl>
            </Grid2>
            <Grid2 size={2}>
              <FormControl
                fullWidth
                error={
                  formik.touched.alertDayLeft2 &&
                  Boolean(formik.errors.alertDayLeft2)
                }
              >
                <InputLabel id="demo-simple-select-helper-label">
                  پیامک دوم
                </InputLabel>
                <Select
                  fullWidth
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  value={menuItems.length > 0 && formik.values.alertDayLeft2}
                  label="پیامک اول"
                  onChange={(event, newValue) =>
                    formik.setFieldValue(
                      "alertDayLeft2",
                      //@ts-ignore
                      newValue?.props?.value
                    )
                  }
                >
                  <MenuItem value="">
                    <em>هیچی</em>
                  </MenuItem>
                  {menuItems}
                </Select>
                <FormHelperText>
                  {
                    (formik.touched.alertDayLeft2 &&
                      formik.errors.alertDayLeft2) as boolean
                  }
                </FormHelperText>
              </FormControl>
            </Grid2>
            <Grid2 size={2}>
              <FormControl
                fullWidth
                error={
                  formik.touched.alertDayLeft3 &&
                  Boolean(formik.errors.alertDayLeft3)
                }
              >
                <InputLabel id="demo-simple-select-helper-label">
                  پیامک سوم
                </InputLabel>
                <Select
                  fullWidth
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  value={menuItems.length > 0 && formik.values.alertDayLeft3}
                  label="پیامک سوم"
                  onChange={(event, newValue) => {
                    formik.setFieldValue(
                      "alertDayLeft3",
                      //@ts-ignore
                      newValue?.props?.value
                    );
                  }}
                >
                  <MenuItem value="">
                    <em>هیچی</em>
                  </MenuItem>
                  {menuItems}
                </Select>
                <FormHelperText>
                  {
                    (formik.touched.alertDayLeft3 &&
                      formik.errors.alertDayLeft3) as boolean
                  }
                </FormHelperText>
              </FormControl>
            </Grid2>
            <Grid2 size={2}>
              <TimePicker
                label="در ساعت"
                onChange={(time: any) => {
                  const hours = time.getHours(); // دریافت ساعت
                  const minutes = time.getMinutes(); // دریافت دقیقه
                  const seconds = time.getSeconds(); // دریافت ثانیه (در صورت نیاز)
                  const timeString = `${hours}:${minutes < 10 ? "0" + minutes : minutes}:${seconds < 10 ? "0" + seconds : seconds}`;
                  formik.setFieldValue("alertTimeAt", timeString);
                }}
                value={alertTime}
              />
            </Grid2>
          </Grid2>
        </AccordionDetails>
      </Accordion>
    </div>
  );
}
